/**
 * Puzzle Service
 *
 * API client for fetching daily puzzles from the backend
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL !== undefined
  ? import.meta.env.VITE_API_BASE_URL
  : 'http://localhost:3007';

export interface PuzzleArticle {
  title: string;
  url: string;
  category: string | null;
}

export interface DailyPuzzle {
  date: string;
  number: number;
  start: PuzzleArticle;
  goal: PuzzleArticle;
}

export interface PuzzleResponse {
  success: boolean;
  puzzle: DailyPuzzle;
}

export interface PuzzleStats {
  totalPuzzles: number;
  firstPuzzle: {
    date: string;
    number: number;
  } | null;
  lastPuzzle: {
    date: string;
    number: number;
  } | null;
  yearsOfContent: string;
}

export interface StatsResponse {
  success: boolean;
  stats: PuzzleStats;
}

export interface PuzzleError {
  error: string;
  date?: string;
  isFuture?: boolean;
}

/**
 * Fetch today's puzzle
 */
export async function getTodaysPuzzle(): Promise<PuzzleResponse> {
  const response = await fetch(`${API_BASE_URL}/api/puzzle/today`);

  if (!response.ok) {
    const error: PuzzleError = await response.json();
    throw new Error(error.error || 'Failed to fetch today\'s puzzle');
  }

  return response.json();
}

/**
 * Fetch puzzle for a specific date (YYYY-MM-DD)
 */
export async function getPuzzleByDate(date: string): Promise<PuzzleResponse> {
  const response = await fetch(`${API_BASE_URL}/api/puzzle/${date}`);

  if (!response.ok) {
    const error: PuzzleError = await response.json();

    if (error.isFuture) {
      throw new Error('FUTURE_DATE');
    }

    throw new Error(error.error || 'Failed to fetch puzzle');
  }

  return response.json();
}

/**
 * Fetch puzzle statistics
 */
export async function getPuzzleStats(): Promise<StatsResponse> {
  const response = await fetch(`${API_BASE_URL}/api/puzzle/stats`);

  if (!response.ok) {
    const error: PuzzleError = await response.json();
    throw new Error(error.error || 'Failed to fetch puzzle stats');
  }

  return response.json();
}

/**
 * Format date as YYYY-MM-DD in UTC
 */
export function formatDate(date: Date): string {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get today's date in YYYY-MM-DD format (UTC)
 */
export function getTodaysDate(): string {
  return formatDate(new Date());
}

/**
 * Validate date format (YYYY-MM-DD)
 */
export function isValidDateFormat(dateString: string): boolean {
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;

  if (!dateRegex.test(dateString)) {
    return false;
  }

  const date = new Date(dateString + 'T00:00:00Z');
  return !isNaN(date.getTime());
}

/**
 * Check if date is in the future
 */
export function isFutureDate(dateString: string): boolean {
  const date = new Date(dateString + 'T00:00:00Z');
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);

  return date > today;
}
